import { useEffect, useRef } from 'react';

function useScrollAnimation(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [ref]);
}

const features = [
  {
    icon: 'ri-group-2-line',
    tag: '身内用',
    title: '中人数の身内で\n楽しめる空間（1卓6人上限）',
    description:
      '秋葉原・神田エリアで、仲間だけで気兼ねなくポーカーを楽しめる貸卓スペース。ポーカーテーブル・チップ・トランプを完備しているため、手ぶらでご利用いただけます。',
    highlight: '手ぶらでOK・仲間だけで楽しめる',
    image:
      'https://static.readdy.ai/image/9f1ed1380ea2c8a902f8bc322070b10e/900b8439f664af7bb92bec249f4c7521.png',
    alt: 'ポーカーレンタルスペース デュエルの店内とポーカーテーブル',
  },
  {
    icon: 'ri-coin-line',
    tag: '低価格',
    title: '1卓550円（税込）/h〜',
    description:
      '1時間1卓550円（税込）〜。1卓最大6名まで利用でき、複数人なら1人あたりの負担を抑えてポーカーを楽しめます。追加料金のない、わかりやすい料金体系です。',
    highlight: '1卓550円〜・わかりやすい料金',
    image:
      'https://static.readdy.ai/image/9f1ed1380ea2c8a902f8bc322070b10e/9bea51384fc55eccf3d08f66f1ec895b.png',
    alt: 'デュエルのポーカー貸卓を利用している店内イメージ',
  },
  {
    icon: 'ri-map-pin-2-line',
    tag: '抜群のアクセス',
    title: '秋葉原・神田から\n徒歩5分・24時間営業',
    description:
      '秋葉原駅・神田駅から徒歩5分。24時間営業・年中無休で、昼間はもちろん、仕事終わりや深夜のポーカー利用にも便利です。',
    highlight: '年中無休・24時間営業',
    image:
      'https://static.readdy.ai/image/9f1ed1380ea2c8a902f8bc322070b10e/99057bb0e97720d4c6b7f91078d4b0f9.png',
    alt: '秋葉原・神田から徒歩圏内のポーカーレンタルスペース デュエル',
  },
  {
    icon: 'ri-cup-line',
    tag: '快適な空間',
    title: '快適な空間',
    description:
      '飲食物の持ち込みOK。Wi-Fi・コンセントを完備し、長時間のポーカー会やボードゲーム利用にも使いやすい空間です。加熱式タバコもご利用いただけます。',
    highlight: '持ち込みOK・加熱式タバコ可',
    image:
      'https://storage.readdy-site.link/project_files/f9bab69d-2fbc-4411-9906-6fecd905051b/85fe4b65-72fa-4b9a-96f4-6a223a7a203e_compressed_ChatGPT-Image-202676-02_31_30.webp',
    alt: '飲食物を持ち込んで利用できるデュエルのポーカースペース',
  },
];

export default function Features() {
  const titleRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useScrollAnimation(titleRef as React.RefObject<HTMLElement>);
  useScrollAnimation(cardsRef as React.RefObject<HTMLElement>);

  return (
    <section id="features" className="bg-[#0a0e1a] py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Title */}
        <div
          ref={titleRef}
          className="fade-in-section text-center mb-14"
        >
          <p className="section-label mb-3">
            WHY デュエル
          </p>

          <h2 className="font-playfair font-bold text-white text-3xl md:text-4xl mb-2">
            秋葉原・神田で楽しめる
            <span className="text-[#d4af37]">
              ポーカーレンタルスペース
            </span>
          </h2>

          <p className="text-[#d4af37]/80 font-noto text-base md:text-lg mb-4 font-medium tracking-wide">
            デュエルが選ばれる4つの理由
          </p>

          <p className="text-white/50 font-noto text-sm md:text-base max-w-lg mx-auto leading-relaxed">
            身内同士で気軽に楽しめる、24時間営業の貸卓スペースです。
          </p>
        </div>

        {/* Cards */}
        <div
          ref={cardsRef}
          className="fade-in-section grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {features.map((feat, i) => (
            <div
              key={feat.tag}
              className="group relative bg-[#1a1f2e] rounded-2xl overflow-hidden border border-[#2a2f3e] hover:border-[#d4af37]/40 transition-all duration-300 hover:-translate-y-1"
              style={{
                transitionDelay: `${i * 80}ms`,
              }}
            >
              {/* Image */}
              <div className="w-full h-56 md:h-64 overflow-hidden relative">
                <img
                  src={feat.image}
                  alt={feat.alt}
                  loading="lazy"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />

                <div className="absolute inset-0 h-56 md:h-64 bg-gradient-to-b from-transparent to-[#1a1f2e]/60" />
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Tag */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i
                      className={`${feat.icon} text-[#d4af37] text-lg`}
                    />
                  </div>

                  <span className="text-[#d4af37] text-xs font-bold tracking-widest uppercase font-noto">
                    {feat.tag}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-playfair font-bold text-white text-xl md:text-2xl mb-3 leading-snug whitespace-pre-line">
                  {feat.title}
                </h3>

                {/* Description */}
                <p className="text-white/55 font-noto text-sm leading-relaxed mb-4">
                  {feat.description}
                </p>

                {/* Highlight */}
                <div className="inline-flex items-center gap-1.5 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full px-3 py-1.5">
                  <i className="ri-sparkling-line text-[#d4af37] text-xs" />

                  <span className="text-[#d4af37] text-xs font-bold font-noto whitespace-nowrap">
                    {feat.highlight}
                  </span>
                </div>
              </div>

              {/* Number */}
              <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-[#0a0e1a]/70 rounded-full border border-[#2a2f3e]">
                <span className="font-playfair font-bold text-[#d4af37] text-sm">
                  {i + 1}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <a
            href="https://upnow.jp/duel"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-4 bg-[#d4af37] text-black font-bold text-base rounded-full hover:bg-[#e8c84a] hover:scale-105 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            今すぐ予約（登録不要）
          </a>
        </div>
      </div>
    </section>
  );
}